import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { customerAPI, productAPI, invoiceAPI, adminAPI, reportAPI, paymentAPI, analyticsAPI, localeAPI } from '../utils/api';
import { formatDateDMY } from '../utils/dateFormat';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

// ═══════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════

/**
 * Format numbers in Indian notation with compact suffixes
 * 1000 → 1K, 100000 → 1L, 10000000 → 1Cr
 */
const formatIndianCompact = (num, decimals = 2) => {
  if (!num || isNaN(num)) return '0';
  const n = Math.abs(num);
  if (n >= 10000000) return `${(num / 10000000).toFixed(decimals)}Cr`;
  if (n >= 100000) return `${(num / 100000).toFixed(decimals)}L`;
  if (n >= 1000) return `${(num / 1000).toFixed(decimals)}K`;
  return num.toLocaleString('en-IN');
};

/**
 * Format currency with Indian compact notation
 */
const formatCurrency = (amount, compact = true) => {
  if (compact) return `₹${formatIndianCompact(amount)}`;
  return `₹${(amount || 0).toLocaleString('en-IN')}`;
};

/**
 * Calculate percentage change
 */
const calcPercentChange = (current, previous) => {
  if (!previous || previous === 0) return 0;
  return ((current - previous) / previous) * 100;
};

/**
 * Get days until a date
 */
const getDaysUntil = (isoDate) => {
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const target = new Date(`${isoDate}T00:00:00`);
  return Math.ceil((target - startOfToday) / (1000 * 60 * 60 * 24));
};

/**
 * Get holiday icon based on name
 */
const getHolidayIcon = (name = '') => {
  const label = name.toLowerCase();
  if (label.includes('diwali')) return '🪔';
  if (label.includes('holi')) return '🎨';
  if (label.includes('janmashtami')) return '🦚';
  if (label.includes('ganesh')) return '🐘';
  if (label.includes('raksha')) return '🧵';
  if (label.includes('rath yatra')) return '🛕';
  if (label.includes('christmas')) return '🎄';
  if (label.includes('independence') || label.includes('republic')) return '🇮🇳';
  if (label.includes('gandhi')) return '🕊️';
  if (label.includes('sankranti') || label.includes('pongal')) return '🪁';
  if (label.includes('shivratri')) return '🔱';
  if (label.includes('ram navami') || label.includes('dussehra')) return '🏹';
  if (label.includes('buddha')) return '🪷';
  if (label.includes('eid') || label.includes('milad') || label.includes('muharram')) return '🕌';
  if (label.includes('onam')) return '🛶';
  if (label.includes('guru nanak')) return '🙏';
  if (label.includes('new year')) return '🎉';
  return '🗓️';
};

// ═══════════════════════════════════════════════════════════════════
// CHART COMPONENTS
// ═══════════════════════════════════════════════════════════════════

/**
 * Enhanced Bar Chart with gradient and hover effects
 */
const EnhancedBarChart = ({ data, darkMode, accent }) => {
  if (!data || data.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: darkMode ? '#64748B' : '#94A3B8' }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>📊</div>
        <p>No chart data available</p>
      </div>
    );
  }

  const maxVal = Math.max(...data.map(d => Math.max(d.sales || 0, d.purchases || 0)), 1);
  const barHeight = 160;

  return (
    <div style={{ padding: '20px 0', overflowX: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, minWidth: data.length * 80, paddingBottom: 30 }}>
        {data.map((d, i) => {
          const sH = Math.max(((d.sales || 0) / maxVal) * barHeight, 3);
          const pH = Math.max(((d.purchases || 0) / maxVal) * barHeight, 3);
          const label = d.month ? formatDateDMY(`${d.month}-01`, '') : '';
          
          return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 60 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', height: barHeight, marginBottom: 8 }}>
                <div 
                  title={`Sales: ${formatCurrency(d.sales || 0)}`}
                  style={{ 
                    width: 20, 
                    height: sH, 
                    background: `linear-gradient(to top, ${accent}, ${accent}dd)`,
                    borderRadius: '6px 6px 0 0',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    boxShadow: `0 -2px 8px ${accent}33`
                  }} 
                />
                <div 
                  title={`Purchases: ${formatCurrency(d.purchases || 0)}`}
                  style={{ 
                    width: 20, 
                    height: pH, 
                    background: darkMode ? 'linear-gradient(to top, #475569, #64748B)' : 'linear-gradient(to top, #94A3B8, #CBD5E1)',
                    borderRadius: '6px 6px 0 0',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    boxShadow: '0 -2px 8px rgba(0,0,0,0.1)'
                  }} 
                />
              </div>
              <span style={{ fontSize: 11, fontWeight: 600, color: darkMode ? '#94A3B8' : '#64748B' }}>{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Mini Sparkline Chart
 */
const Sparkline = ({ data, color, height = 40 }) => {
  if (!data || data.length < 2) return null;
  
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  
  const points = data.map((val, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = height - ((val - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg width="100%" height={height} style={{ display: 'block' }}>
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// ═══════════════════════════════════════════════════════════════════
// KPI CARD COMPONENTS
// ═══════════════════════════════════════════════════════════════════

/**
 * Modern KPI Card with trend indicator
 */
const KPICard = ({ title, value, icon, trend, trendValue, sparklineData, color, darkMode, accent, link }) => {
  const colorMap = {
    blue: { bg: darkMode ? '#1E3A5F' : '#EFF6FF', text: '#3B82F6', light: '#DBEAFE' },
    green: { bg: darkMode ? '#064E3B' : '#ECFDF5', text: '#10B981', light: '#D1FAE5' },
    red: { bg: darkMode ? '#7F1D1D' : '#FEF2F2', text: '#EF4444', light: '#FEE2E2' },
    orange: { bg: darkMode ? '#78350F' : '#FFF7ED', text: '#F97316', light: '#FFEDD5' },
    purple: { bg: darkMode ? '#4C1D95' : '#FAF5FF', text: '#A855F7', light: '#F3E8FF' },
    indigo: { bg: darkMode ? '#312E81' : '#EEF2FF', text: '#6366F1', light: '#E0E7FF' },
  };
  
  const c = colorMap[color] || colorMap.blue;
  const cardBg = darkMode ? '#1E293B' : '#FFFFFF';
  const borderColor = darkMode ? '#334155' : '#E2E8F0';
  
  const trendColor = trend === 'up' ? '#10B981' : trend === 'down' ? '#EF4444' : '#94A3B8';
  const trendIcon = trend === 'up' ? '↗' : trend === 'down' ? '↘' : '→';
  
  const CardContent = (
    <div style={{
      background: cardBg,
      border: `1px solid ${borderColor}`,
      borderRadius: 16,
      padding: 20,
      boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
      transition: 'all 0.2s ease',
      cursor: link ? 'pointer' : 'default',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background gradient effect */}
      <div style={{
        position: 'absolute',
        top: -50,
        right: -50,
        width: 150,
        height: 150,
        background: `radial-gradient(circle, ${c.bg} 0%, transparent 70%)`,
        opacity: 0.4,
        pointerEvents: 'none'
      }} />
      
      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: c.bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 20,
            boxShadow: `0 4px 12px ${c.bg}88`
          }}>
            {icon}
          </div>
          {trend && trendValue !== undefined && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '4px 10px',
              borderRadius: 8,
              background: trend === 'up' ? '#ECFDF5' : trend === 'down' ? '#FEF2F2' : '#F1F5F9',
              fontSize: 11,
              fontWeight: 700,
              color: trendColor
            }}>
              <span style={{ fontSize: 13 }}>{trendIcon}</span>
              {Math.abs(trendValue).toFixed(1)}%
            </div>
          )}
        </div>
        
        {/* Value */}
        <div style={{ marginBottom: 8 }}>
          <div style={{
            fontSize: 28,
            fontWeight: 800,
            color: darkMode ? '#F1F5F9' : '#0F172A',
            lineHeight: 1.2,
            letterSpacing: '-0.02em'
          }}>
            {value}
          </div>
        </div>
        
        {/* Title */}
        <div style={{
          fontSize: 13,
          fontWeight: 600,
          color: darkMode ? '#94A3B8' : '#64748B',
          marginBottom: sparklineData ? 8 : 0
        }}>
          {title}
        </div>
        
        {/* Sparkline */}
        {sparklineData && sparklineData.length > 0 && (
          <div style={{ marginTop: 8, opacity: 0.7 }}>
            <Sparkline data={sparklineData} color={c.text} height={30} />
          </div>
        )}
      </div>
    </div>
  );
  
  return link ? <Link to={link} style={{ textDecoration: 'none' }}>{CardContent}</Link> : CardContent;
};

/**
 * Metric Card for simple stats
 */
const MetricCard = ({ label, value, icon, color, darkMode }) => {
  const textColor = darkMode ? '#F1F5F9' : '#0F172A';
  const mutedColor = darkMode ? '#94A3B8' : '#64748B';
  
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: 16,
      background: darkMode ? '#1E293B' : '#FFFFFF',
      border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`,
      borderRadius: 12,
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
    }}>
      <span style={{ fontSize: 24 }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: textColor, lineHeight: 1 }}>
          {value}
        </div>
        <div style={{ fontSize: 11, fontWeight: 600, color: mutedColor, marginTop: 4 }}>
          {label}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// MAIN DASHBOARD COMPONENT
// ═══════════════════════════════════════════════════════════════════

const Dashboard = () => {
  const { user, userType } = useAuth();
  const { darkMode, color } = useTheme();
  const accent = color.swatch;
  
  // State management
  const [customerStats, setCustomerStats] = useState(null);
  const [productStats, setProductStats] = useState(null);
  const [invoiceStats, setInvoiceStats] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [adminClientCount, setAdminClientCount] = useState(null);
  const [dashSummary, setDashSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [paymentReminders, setPaymentReminders] = useState([]);
  const [healthScore, setHealthScore] = useState(null);
  const [upcomingHolidays, setUpcomingHolidays] = useState([]);
  const [holidaySource, setHolidaySource] = useState(null);
  const containerRef = useRef(null);

  // Keyboard navigation
  const handleDashboardKeys = useCallback((e) => {
    const tag = document.activeElement?.tagName?.toLowerCase();
    const inInput = tag === 'input' || tag === 'textarea' || tag === 'select';
    if (inInput || !containerRef.current) return;

    if (['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      const focusable = Array.from(containerRef.current.querySelectorAll('a[href], button:not([disabled]), [tabindex="0"]'));
      if (focusable.length === 0) return;
      
      const idx = focusable.indexOf(document.activeElement);
      let next;
      
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        next = idx < focusable.length - 1 ? idx + 1 : 0;
      } else {
        next = idx > 0 ? idx - 1 : focusable.length - 1;
      }
      
      e.preventDefault();
      focusable[next]?.focus();
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleDashboardKeys);
    return () => window.removeEventListener('keydown', handleDashboardKeys);
  }, [handleDashboardKeys]);

  // Data fetching
  useEffect(() => {
    if (userType === 'admin') {
      adminAPI.listClients()
        .then(r => {
          const list = r.data.clients || r.data || [];
          setAdminClientCount(list.length);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      Promise.all([
        customerAPI.stats().catch(() => null),
        productAPI.stats().catch(() => null),
        invoiceAPI.stats().catch(() => null),
        invoiceAPI.list({ page: 1, limit: 5 }).catch(() => null),
        reportAPI.dashboardSummary().catch(() => null),
        paymentAPI.summary().catch(() => null),
        paymentAPI.reminders().catch(() => null),
        analyticsAPI.businessHealth().catch(() => null),
        localeAPI.holidays({ upcoming_only: true, limit: 6 }).catch(() => null),
      ]).then(([cRes, pRes, iRes, invRes, dashRes, paySumRes, payRemRes, healthRes, holidayRes]) => {
        if (cRes) setCustomerStats(cRes.data);
        if (pRes) setProductStats(pRes.data);
        if (iRes) setInvoiceStats(iRes.data);
        if (invRes) setRecentInvoices(invRes.data.invoices || []);
        if (dashRes) setDashSummary(dashRes.data);
        if (paySumRes) setPaymentSummary(paySumRes.data);
        if (payRemRes) setPaymentReminders(Array.isArray(payRemRes.data) ? payRemRes.data : []);
        if (healthRes) setHealthScore(healthRes.data);
        if (holidayRes?.data) {
          setUpcomingHolidays(Array.isArray(holidayRes.data.items) ? holidayRes.data.items : []);
          setHolidaySource(holidayRes.data.source || null);
        }
        setLoading(false);
      });
    }
  }, [userType]);

  // Style constants
  const textColor = darkMode ? '#F1F5F9' : '#0F172A';
  const mutedColor = darkMode ? '#94A3B8' : '#64748B';
  const borderColor = darkMode ? '#334155' : '#E2E8F0';
  const cardBg = darkMode ? '#1E293B' : '#FFFFFF';
  const bgColor = darkMode ? '#0F172A' : '#F8FAFC';

  // Quick action buttons
  const quickActions = [
    { to: '/billing?action=create&source=%2Fdashboard', icon: '🧾', label: 'Invoice', hotkey: 'F1', color: '#3B82F6' },
    { to: '/billing?action=create&mode=purchase&source=%2Fdashboard', icon: '🏭', label: 'Purchase', hotkey: 'F2', color: '#8B5CF6' },
    { to: '/products?action=create&source=%2Fdashboard', icon: '📦', label: 'Product', hotkey: 'F3', color: '#10B981' },
    { to: '/parties?action=create&source=%2Fdashboard', icon: '👤', label: 'Customer', hotkey: 'F4', color: '#F59E0B' },
    { to: '/parties?action=create&type=supplier&source=%2Fdashboard', icon: '🤝', label: 'Supplier', hotkey: 'F5', color: '#EF4444' },
    { to: '/quotations?action=create&source=%2Fdashboard', icon: '📋', label: 'Quote', hotkey: 'F6', color: '#06B6D4' },
    { to: '/payments?action=create&source=%2Fdashboard', icon: '💳', label: 'Payment', hotkey: 'F7', color: '#EC4899' },
    { to: '/marketing?action=compose&source=%2Fdashboard', icon: '✉️', label: 'Message', hotkey: 'F8', color: '#6366F1' },
  ];

  return (
    <Layout>
      <div
        ref={containerRef}
        style={{
          maxWidth: 1600,
          margin: '0 auto',
          padding: '0 16px'
        }}
        className="page-enter"
      >
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <h1 style={{
            fontSize: 32,
            fontWeight: 900,
            color: textColor,
            marginBottom: 8,
            letterSpacing: '-0.02em'
          }}>
            Welcome back, {user?.owner_name || user?.email || 'User'}! 👋
          </h1>
          <p style={{ fontSize: 15, color: mutedColor, fontWeight: 500 }}>
            {formatDateDMY(new Date())}
          </p>
        </div>

        {loading ? (
          <div style={{
            textAlign: 'center',
            padding: 80,
            color: mutedColor
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
            <p style={{ fontSize: 16, fontWeight: 600 }}>Loading your dashboard...</p>
          </div>
        ) : userType === 'admin' ? (
          /* ═══════════════════════════════════════════════════════════════
             ADMIN DASHBOARD
             ═══════════════════════════════════════════════════════════════ */
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div style={{
                background: `linear-gradient(135deg, ${accent} 0%, ${accent}dd 100%)`,
                borderRadius: 20,
                padding: 40,
                color: '#FFFFFF',
                boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
              }}>
                <div style={{ fontSize: 56, fontWeight: 900, marginBottom: 8, letterSpacing: '-0.03em' }}>
                  {adminClientCount ?? 0}
                </div>
                <div style={{ fontSize: 18, fontWeight: 600, opacity: 0.95 }}>
                  Registered Shop Owners
                </div>
              </div>
              
              <div style={{
                background: cardBg,
                border: `1px solid ${borderColor}`,
                borderRadius: 20,
                padding: 40,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: textColor, marginBottom: 8 }}>
                  Super Admin Portal
                </div>
                <p style={{ fontSize: 14, color: mutedColor }}>
                  Manage shop owner accounts, subscriptions, and platform settings
                </p>
              </div>
            </div>

            <div style={{
              background: cardBg,
              border: `1px solid ${borderColor}`,
              borderRadius: 20,
              padding: 32,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
            }}>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: textColor, marginBottom: 24 }}>
                Quick Actions
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { to: '/admin', icon: '🏪', label: 'Manage Shop Owners', desc: 'View and manage clients' },
                  { to: '/settings', icon: '⚙️', label: 'Settings', desc: 'Platform configuration' },
                ].map(action => (
                  <Link
                    key={action.to}
                    to={action.to}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 16,
                      padding: 20,
                      borderRadius: 12,
                      border: `1px solid ${borderColor}`,
                      textDecoration: 'none',
                      transition: 'all 0.2s ease',
                      background: darkMode ? '#0F172A' : '#F8FAFC'
                    }}
                  >
                    <span style={{ fontSize: 32 }}>{action.icon}</span>
                    <div>
                      <p style={{ fontWeight: 700, fontSize: 15, color: textColor, margin: 0 }}>
                        {action.label}
                      </p>
                      <p style={{ fontSize: 12, color: mutedColor, margin: '4px 0 0' }}>
                        {action.desc}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </>
        ) : (
          /* ═══════════════════════════════════════════════════════════════
             CLIENT DASHBOARD
             ═══════════════════════════════════════════════════════════════ */
          <>
            {/* Quick Actions Bar */}
            <div style={{
              background: cardBg,
              border: `1px solid ${borderColor}`,
              borderRadius: 16,
              padding: 16,
              marginBottom: 24,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
            }}>
              <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
                {quickActions.map(action => (
                  <Link
                    key={action.to}
                    to={action.to}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      padding: '16px 8px',
                      borderRadius: 12,
                      border: `1px solid ${borderColor}`,
                      textDecoration: 'none',
                      transition: 'all 0.2s ease',
                      background: darkMode ? '#0F172A' : '#F8FAFC'
                    }}
                  >
                    <span style={{ fontSize: 24 }}>{action.icon}</span>
                    <span style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: textColor,
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%'
                    }}>
                      {action.label}
                    </span>
                    <span style={{
                      fontSize: 9,
                      fontWeight: 600,
                      color: mutedColor,
                      padding: '2px 6px',
                      borderRadius: 4,
                      background: darkMode ? '#1E293B' : '#E2E8F0'
                    }}>
                      {action.hotkey}
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Primary KPIs - Hero Section */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {/* Revenue Card */}
              <div style={{
                background: `linear-gradient(135deg, #10B981 0%, #059669 100%)`,
                borderRadius: 20,
                padding: 32,
                color: '#FFFFFF',
                boxShadow: '0 10px 40px rgba(16, 185, 129, 0.3)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: -30,
                  right: -30,
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.1)',
                  pointerEvents: 'none'
                }} />
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, opacity: 0.9, marginBottom: 12 }}>
                    💰 Total Revenue
                  </div>
                  <div style={{ fontSize: 40, fontWeight: 900, letterSpacing: '-0.02em', marginBottom: 8 }}>
                    {formatCurrency(invoiceStats?.total_revenue || 0, true)}
                  </div>
                  <div style={{ fontSize: 13, opacity: 0.85 }}>
                    {invoiceStats?.total_invoices || 0} total invoices
                  </div>
                </div>
              </div>

              {/* Pending Amount Card */}
              <div style={{
                background: `linear-gradient(135deg, #EF4444 0%, #DC2626 100%)`,
                borderRadius: 20,
                padding: 32,
                color: '#FFFFFF',
                boxShadow: '0 10px 40px rgba(239, 68, 68, 0.3)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: -30,
                  right: -30,
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.1)',
                  pointerEvents: 'none'
                }} />
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, opacity: 0.9, marginBottom: 12 }}>
                    ⏳ Pending Amount
                  </div>
                  <div style={{ fontSize: 40, fontWeight: 900, letterSpacing: '-0.02em', marginBottom: 8 }}>
                    {formatCurrency(invoiceStats?.pending_amount || 0, true)}
                  </div>
                  <div style={{ fontSize: 13, opacity: 0.85 }}>
                    {dashSummary?.overdue_invoices?.length || 0} overdue invoices
                  </div>
                </div>
              </div>

              {/* Net Balance Card */}
              <div style={{
                background: `linear-gradient(135deg, ${accent} 0%, ${accent}dd 100%)`,
                borderRadius: 20,
                padding: 32,
                color: '#FFFFFF',
                boxShadow: `0 10px 40px ${accent}44`,
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: -30,
                  right: -30,
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.1)',
                  pointerEvents: 'none'
                }} />
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, opacity: 0.9, marginBottom: 12 }}>
                    📊 Net Balance
                  </div>
                  <div style={{ fontSize: 40, fontWeight: 900, letterSpacing: '-0.02em', marginBottom: 8 }}>
                    {formatCurrency((paymentSummary?.total_pay_in || 0) - (paymentSummary?.total_pay_out || 0), true)}
                  </div>
                  <div style={{ fontSize: 13, opacity: 0.85 }}>
                    In: {formatCurrency(paymentSummary?.total_pay_in || 0)} | Out: {formatCurrency(paymentSummary?.total_pay_out || 0)}
                  </div>
                </div>
              </div>
            </div>

            {/* Secondary KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
              <KPICard
                title="Customers"
                value={customerStats?.total_customers || 0}
                icon="👥"
                color="blue"
                darkMode={darkMode}
                accent={accent}
                link="/parties"
              />
              <KPICard
                title="Products"
                value={productStats?.total_products || 0}
                icon="📦"
                color="indigo"
                darkMode={darkMode}
                accent={accent}
                link="/products"
              />
              <KPICard
                title="Stock Value"
                value={formatCurrency(dashSummary?.stock_retail_value || 0)}
                icon="💎"
                color="purple"
                darkMode={darkMode}
                accent={accent}
                link="/products"
              />
              <KPICard
                title="This Month"
                value={invoiceStats?.invoices_this_month || 0}
                icon="📅"
                color="green"
                darkMode={darkMode}
                accent={accent}
              />
              <KPICard
                title="Pay-In"
                value={formatCurrency(paymentSummary?.total_pay_in || 0)}
                icon="💵"
                color="green"
                darkMode={darkMode}
                accent={accent}
                link="/payments"
              />
              <KPICard
                title="Pay-Out"
                value={formatCurrency(paymentSummary?.total_pay_out || 0)}
                icon="💸"
                color="red"
                darkMode={darkMode}
                accent={accent}
                link="/payments"
              />
            </div>

            {/* Outstanding Amounts Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <MetricCard
                label="Customer Outstanding"
                value={formatCurrency(dashSummary?.customer_outstanding || 0)}
                icon="💸"
                darkMode={darkMode}
              />
              <MetricCard
                label="Supplier Outstanding"
                value={formatCurrency(dashSummary?.supplier_outstanding || 0)}
                icon="🏭"
                darkMode={darkMode}
              />
              <MetricCard
                label="Stock Units"
                value={formatIndianCompact(dashSummary?.stock_total_units || 0, 1)}
                icon="📊"
                darkMode={darkMode}
              />
            </div>

            {/* Alerts - Stock Issues */}
            {productStats && (productStats.low_stock > 0 || productStats.out_of_stock > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {productStats.out_of_stock > 0 && (
                  <Link
                    to="/products"
                    style={{
                      background: darkMode ? '#7F1D1D' : '#FEF2F2',
                      border: `2px solid ${darkMode ? '#991B1B' : '#FCA5A5'}`,
                      borderRadius: 16,
                      padding: 20,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 16,
                      textDecoration: 'none',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{
                      width: 56,
                      height: 56,
                      borderRadius: 12,
                      background: darkMode ? '#991B1B' : '#FEE2E2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 28
                    }}>
                      ❌
                    </div>
                    <div>
                      <div style={{
                        fontWeight: 800,
                        fontSize: 18,
                        color: darkMode ? '#FCA5A5' : '#991B1B',
                        marginBottom: 4
                      }}>
                        {productStats.out_of_stock} Out of Stock
                      </div>
                      <div style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: darkMode ? '#F87171' : '#DC2626'
                      }}>
                        Urgent: Restock immediately →
                      </div>
                    </div>
                  </Link>
                )}
                {productStats.low_stock > 0 && (
                  <Link
                    to="/products"
                    style={{
                      background: darkMode ? '#78350F' : '#FFFBEB',
                      border: `2px solid ${darkMode ? '#92400E' : '#FCD34D'}`,
                      borderRadius: 16,
                      padding: 20,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 16,
                      textDecoration: 'none',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{
                      width: 56,
                      height: 56,
                      borderRadius: 12,
                      background: darkMode ? '#92400E' : '#FEF3C7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 28
                    }}>
                      ⚠️
                    </div>
                    <div>
                      <div style={{
                        fontWeight: 800,
                        fontSize: 18,
                        color: darkMode ? '#FCD34D' : '#92400E',
                        marginBottom: 4
                      }}>
                        {productStats.low_stock} Low Stock
                      </div>
                      <div style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: darkMode ? '#FBBF24' : '#B45309'
                      }}>
                        Action needed: Reorder soon →
                      </div>
                    </div>
                  </Link>
                )}
              </div>
            )}

            {/* Business Health Score */}
            {healthScore && (
              <div style={{
                background: cardBg,
                border: `1px solid ${borderColor}`,
                borderRadius: 20,
                padding: 32,
                marginBottom: 24,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 24
                }}>
                  <h2 style={{ fontSize: 20, fontWeight: 800, color: textColor }}>
                    🔬 Business Health Analytics
                  </h2>
                  <Link
                    to="/analytics"
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: accent,
                      textDecoration: 'none'
                    }}
                  >
                    Detailed Analytics →
                  </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                  {/* Health Score Circle */}
                  <div className="md:col-span-3" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div style={{ position: 'relative', width: 140, height: 140 }}>
                      <svg viewBox="0 0 36 36" style={{ width: 140, height: 140, transform: 'rotate(-90deg)' }}>
                        <circle
                          cx="18"
                          cy="18"
                          r="15.9"
                          fill="none"
                          stroke={darkMode ? '#1E293B' : '#E2E8F0'}
                          strokeWidth="2.5"
                        />
                        <circle
                          cx="18"
                          cy="18"
                          r="15.9"
                          fill="none"
                          stroke={
                            healthScore.score >= 80 ? '#10B981' :
                            healthScore.score >= 60 ? '#F59E0B' :
                            healthScore.score >= 40 ? '#F97316' : '#EF4444'
                          }
                          strokeWidth="2.5"
                          strokeDasharray={`${healthScore.score} ${100 - healthScore.score}`}
                          strokeLinecap="round"
                        />
                      </svg>
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <span style={{
                          fontSize: 36,
                          fontWeight: 900,
                          color: healthScore.score >= 80 ? '#10B981' :
                                 healthScore.score >= 60 ? '#F59E0B' :
                                 healthScore.score >= 40 ? '#F97316' : '#EF4444'
                        }}>
                          {healthScore.score}
                        </span>
                        <span style={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: mutedColor,
                          marginTop: 4
                        }}>
                          {healthScore.grade}
                        </span>
                      </div>
                    </div>
                    <div style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: mutedColor,
                      textAlign: 'center',
                      marginTop: 12
                    }}>
                      Overall Health Score
                    </div>
                  </div>

                  {/* Breakdown Metrics */}
                  <div className="md:col-span-5" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {healthScore.breakdown && Object.entries(healthScore.breakdown).slice(0, 5).map(([key, val]) => {
                      const percentage = Math.min(Math.round((Number(val) / 25) * 100), 100);
                      const metricColor = val >= 17 ? '#10B981' : val >= 10 ? '#F59E0B' : '#EF4444';
                      
                      return (
                        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: mutedColor,
                            width: 120,
                            textTransform: 'capitalize'
                          }}>
                            {key.replace(/_/g, ' ')}
                          </span>
                          <div style={{
                            flex: 1,
                            height: 8,
                            borderRadius: 4,
                            background: darkMode ? '#1E293B' : '#E2E8F0',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${percentage}%`,
                              height: '100%',
                              background: metricColor,
                              transition: 'width 0.5s ease'
                            }} />
                          </div>
                          <span style={{
                            fontSize: 13,
                            fontWeight: 800,
                            color: metricColor,
                            width: 36,
                            textAlign: 'right'
                          }}>
                            {Math.round(Number(val) || 0)}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Insights */}
                  <div className="md:col-span-4" style={{
                    background: darkMode ? '#0F172A' : '#F8FAFC',
                    borderRadius: 12,
                    padding: 20
                  }}>
                    <div style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: textColor,
                      marginBottom: 12
                    }}>
                      💡 Key Insights
                    </div>
                    {healthScore.insights && healthScore.insights.slice(0, 3).map((tip, i) => (
                      <div
                        key={i}
                        style={{
                          fontSize: 12,
                          color: mutedColor,
                          marginBottom: 10,
                          lineHeight: 1.5,
                          paddingLeft: 16,
                          position: 'relative'
                        }}
                      >
                        <span style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          fontWeight: 700,
                          color: accent
                        }}>
                          •
                        </span>
                        {tip.message || tip}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Payment Reminders */}
            {paymentReminders.length > 0 && (
              <div style={{
                background: cardBg,
                border: `1px solid ${borderColor}`,
                borderRadius: 20,
                marginBottom: 24,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '20px 32px',
                  borderBottom: `1px solid ${borderColor}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <h2 style={{ fontSize: 20, fontWeight: 800, color: textColor }}>
                    🔔 Payment Reminders
                  </h2>
                  <span style={{
                    fontSize: 12,
                    fontWeight: 700,
                    padding: '6px 14px',
                    borderRadius: 8,
                    background: darkMode ? '#78350F' : '#FEF3C7',
                    color: '#D97706'
                  }}>
                    {paymentReminders.length} active
                  </span>
                </div>
                <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                  {paymentReminders.slice(0, 8).map((r, i) => (
                    <div
                      key={r.id}
                      style={{
                        padding: '16px 32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderTop: i > 0 ? `1px solid ${borderColor}` : 'none',
                        flexWrap: 'wrap',
                        gap: 12
                      }}
                    >
                      <div style={{ minWidth: 250, flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, color: textColor, marginBottom: 4 }}>
                          {r.payment_mode === 'Cheque' ? '🏦' : '🏢'} {r.payment_mode} — {formatCurrency(r.amount)}
                        </div>
                        <div style={{ fontSize: 12, color: mutedColor }}>
                          {r.payment_mode === 'Cheque'
                            ? `Bank: ${r.cheque_bank || '—'} | #${r.cheque_number || '—'} | ${r.cheque_date ? formatDateDMY(r.cheque_date) : '—'}`
                            : `Company: ${r.finance_company || '—'} | EMI: ${formatCurrency(r.finance_emi_amount)} | Due: ${r.finance_emi_date ? formatDateDMY(r.finance_emi_date) : '—'}`}
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            await paymentAPI.dismissReminder(r.id);
                            setPaymentReminders(prev => prev.filter(x => x.id !== r.id));
                          } catch (e) {
                            console.error(e);
                          }
                        }}
                        style={{
                          padding: '8px 16px',
                          background: accent,
                          color: '#FFFFFF',
                          border: 'none',
                          borderRadius: 8,
                          fontWeight: 700,
                          fontSize: 12,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        ✓ Dismiss
                      </button>
                    </div>
                  ))}
                </div>
                {paymentReminders.length > 8 && (
                  <div style={{
                    padding: '16px 32px',
                    textAlign: 'center',
                    borderTop: `1px solid ${borderColor}`
                  }}>
                    <Link
                      to="/payments"
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: accent,
                        textDecoration: 'none'
                      }}
                    >
                      View all {paymentReminders.length} reminders →
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Charts & Calendar Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* Sales vs Purchases Chart */}
              <div className="lg:col-span-2" style={{
                background: cardBg,
                border: `1px solid ${borderColor}`,
                borderRadius: 20,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '20px 32px',
                  borderBottom: `1px solid ${borderColor}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 12
                }}>
                  <h2 style={{ fontSize: 20, fontWeight: 800, color: textColor }}>
                    📈 Sales vs Purchases
                  </h2>
                  <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600 }}>
                      <span style={{
                        width: 12,
                        height: 12,
                        borderRadius: 3,
                        background: accent
                      }} />
                      <span style={{ color: mutedColor }}>Sales</span>
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600 }}>
                      <span style={{
                        width: 12,
                        height: 12,
                        borderRadius: 3,
                        background: darkMode ? '#64748B' : '#94A3B8'
                      }} />
                      <span style={{ color: mutedColor }}>Purchases</span>
                    </span>
                  </div>
                </div>
                <div style={{ padding: '8px 24px 24px' }}>
                  <EnhancedBarChart
                    data={dashSummary?.chart_data || []}
                    darkMode={darkMode}
                    accent={accent}
                  />
                </div>
              </div>

              {/* Upcoming Holidays */}
              <div style={{
                background: cardBg,
                border: `1px solid ${borderColor}`,
                borderRadius: 20,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '20px 24px',
                  borderBottom: `1px solid ${borderColor}`
                }}>
                  <h2 style={{ fontSize: 18, fontWeight: 800, color: textColor, marginBottom: 4 }}>
                    🎉 Upcoming Holidays
                  </h2>
                  {holidaySource?.label && (
                    <p style={{ fontSize: 11, color: mutedColor, margin: 0 }}>
                      {holidaySource.label}
                    </p>
                  )}
                </div>
                <div style={{ padding: 16, maxHeight: 400, overflowY: 'auto' }}>
                  {upcomingHolidays.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '40px 16px',
                      color: mutedColor
                    }}>
                      <div style={{ fontSize: 36, marginBottom: 8 }}>📅</div>
                      <p style={{ fontSize: 13 }}>Holiday calendar unavailable</p>
                    </div>
                  ) : (
                    upcomingHolidays.map((holiday, i) => {
                      const daysUntil = getDaysUntil(holiday.date);
                      const validDays = Number.isFinite(daysUntil);
                      const badgeLabel = holiday.holiday_type === 'GH' ? 'Gazetted' : 
                                        holiday.holiday_type === 'RH' ? 'Restricted' : 
                                        holiday.holiday_type;
                      
                      return (
                        <div
                          key={`${holiday.date}-${holiday.name}`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            padding: '12px 8px',
                            borderBottom: i < upcomingHolidays.length - 1 ? `1px solid ${borderColor}` : 'none'
                          }}
                        >
                          <span style={{ fontSize: 24 }}>{getHolidayIcon(holiday.name)}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontWeight: 700,
                              fontSize: 13,
                              color: textColor,
                              marginBottom: 2
                            }}>
                              {holiday.name}
                            </div>
                            <div style={{ fontSize: 11, color: mutedColor }}>
                              {formatDateDMY(holiday.date)}
                            </div>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                            <span style={{
                              fontSize: 9,
                              fontWeight: 700,
                              padding: '3px 8px',
                              borderRadius: 6,
                              background: holiday.holiday_type === 'GH'
                                ? (darkMode ? '#064E3B' : '#DCFCE7')
                                : holiday.holiday_type === 'RH'
                                ? (darkMode ? '#78350F' : '#FEF3C7')
                                : (darkMode ? '#1E3A8A' : '#DBEAFE'),
                              color: holiday.holiday_type === 'GH'
                                ? '#22C55E'
                                : holiday.holiday_type === 'RH'
                                ? '#D97706'
                                : '#2563EB'
                            }}>
                              {badgeLabel}
                            </span>
                            <span style={{
                              fontSize: 11,
                              fontWeight: 700,
                              padding: '4px 10px',
                              borderRadius: 6,
                              background: validDays && daysUntil <= 7
                                ? (darkMode ? '#7F1D1D' : '#FEF2F2')
                                : (darkMode ? '#1E293B' : '#F1F5F9'),
                              color: validDays && daysUntil <= 7
                                ? '#EF4444'
                                : mutedColor
                            }}>
                              {!validDays ? '-' : daysUntil === 0 ? 'Today!' : daysUntil === 1 ? 'Tomorrow' : `${daysUntil}d`}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <Link
                    to="/marketing"
                    style={{
                      display: 'block',
                      textAlign: 'center',
                      marginTop: 16,
                      fontSize: 13,
                      fontWeight: 600,
                      color: accent,
                      textDecoration: 'none'
                    }}
                  >
                    📣 Send Holiday Greetings →
                  </Link>
                </div>
              </div>
            </div>

            {/* Recovery & Recent Invoices Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* Recovery Reminders */}
              <div className="lg:col-span-2" style={{
                background: cardBg,
                border: `1px solid ${borderColor}`,
                borderRadius: 20,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '20px 32px',
                  borderBottom: `1px solid ${borderColor}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <h2 style={{ fontSize: 20, fontWeight: 800, color: textColor }}>
                    ⏰ Recovery Reminders
                  </h2>
                  <span style={{
                    fontSize: 12,
                    fontWeight: 700,
                    padding: '6px 14px',
                    borderRadius: 8,
                    background: darkMode ? '#7F1D1D' : '#FEF2F2',
                    color: '#EF4444'
                  }}>
                    {dashSummary?.overdue_invoices?.length || 0} overdue
                  </span>
                </div>
                {(!dashSummary?.overdue_invoices || dashSummary.overdue_invoices.length === 0) ? (
                  <div style={{
                    textAlign: 'center',
                    padding: 60,
                    color: mutedColor
                  }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
                    <p style={{ fontSize: 16, fontWeight: 600 }}>All caught up!</p>
                    <p style={{ fontSize: 13 }}>No overdue invoices — great job!</p>
                  </div>
                ) : (
                  <div style={{ maxHeight: 380, overflowY: 'auto' }}>
                    {dashSummary.overdue_invoices.map((inv, i) => (
                      <div
                        key={inv.id}
                        style={{
                          padding: '16px 32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderTop: i > 0 ? `1px solid ${borderColor}` : 'none',
                          flexWrap: 'wrap',
                          gap: 12
                        }}
                      >
                        <div style={{ minWidth: 220, flex: 1 }}>
                          <div style={{
                            fontWeight: 700,
                            fontSize: 15,
                            color: textColor,
                            marginBottom: 4
                          }}>
                            {inv.customer_name}
                          </div>
                          <div style={{ fontSize: 12, color: mutedColor }}>
                            {inv.invoice_number} · {inv.days_old}d overdue
                            {inv.phone && ` · 📞 ${inv.phone}`}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{
                            fontWeight: 900,
                            fontSize: 18,
                            color: '#EF4444',
                            marginBottom: 2
                          }}>
                            {formatCurrency(inv.due)}
                          </div>
                          <span style={{ fontSize: 11, color: mutedColor }}>
                            of {formatCurrency(inv.grand_total)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent Invoices */}
              <div style={{
                background: cardBg,
                border: `1px solid ${borderColor}`,
                borderRadius: 20,
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                overflow: 'hidden'
              }}>
                <div style={{
                  padding: '20px 24px',
                  borderBottom: `1px solid ${borderColor}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <h2 style={{ fontSize: 18, fontWeight: 800, color: textColor }}>
                    Recent Invoices
                  </h2>
                  <Link
                    to="/billing"
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: accent,
                      textDecoration: 'none'
                    }}
                  >
                    View all →
                  </Link>
                </div>
                {recentInvoices.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: 60,
                    color: mutedColor
                  }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>🧾</div>
                    <p style={{ fontSize: 14, marginBottom: 8 }}>No invoices yet</p>
                    <Link
                      to="/billing"
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: accent,
                        textDecoration: 'none'
                      }}
                    >
                      Create your first invoice →
                    </Link>
                  </div>
                ) : (
                  <div>
                    {recentInvoices.map((inv, i) => (
                      <div
                        key={inv.id}
                        style={{
                          padding: '16px 24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderTop: i > 0 ? `1px solid ${borderColor}` : 'none'
                        }}
                      >
                        <div>
                          <div style={{
                            fontWeight: 700,
                            fontSize: 14,
                            color: textColor,
                            marginBottom: 4
                          }}>
                            {inv.invoice_number}
                          </div>
                          <div style={{ fontSize: 12, color: mutedColor }}>
                            {inv.customer_name} · {formatDateDMY(inv.created_at)}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{
                            fontWeight: 800,
                            fontSize: 14,
                            color: textColor,
                            marginBottom: 4
                          }}>
                            {formatCurrency(inv.grand_total)}
                          </div>
                          <span style={{
                            fontSize: 10,
                            fontWeight: 700,
                            padding: '3px 10px',
                            borderRadius: 6,
                            background: inv.payment_status === 'paid'
                              ? (darkMode ? '#064E3B' : '#D1FAE5')
                              : inv.payment_status === 'partial'
                              ? (darkMode ? '#78350F' : '#FEF3C7')
                              : (darkMode ? '#7F1D1D' : '#FEE2E2'),
                            color: inv.payment_status === 'paid'
                              ? (darkMode ? '#6EE7B7' : '#065F46')
                              : inv.payment_status === 'partial'
                              ? (darkMode ? '#FCD34D' : '#92400E')
                              : (darkMode ? '#FCA5A5' : '#991B1B'),
                            textTransform: 'uppercase'
                          }}>
                            {inv.payment_status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;